import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppMenuCheck } from "./AppMenuCheck";

// AppMarkRead - the settings menu's "Mark chats read on WhatsApp when I open them" (2026-10-14), default ON, kept in this
// browser like the theme (`localStorage` "wac.markRead", "0" = off). While on, an open chat in a VISIBLE tab asks the
// service to mark THEIR newest messages read (`/live` `read`, `Bridge/src/ReadReceipts.ts`); the contact then sees blue
// ticks - unless the owner turned read receipts off in WhatsApp's own privacy settings, which still applies.
// ⚠ Reaches `localStorage` / `document` through `globalThis` (the Bridge typecheck has no DOM lib); every access wrapped.

interface IMrStore { getItem(K: string): string | null; setItem(K: string, V: string): void; }
interface IMrNode { closest?(S: string): IMrNode | null; setAttribute(N: string, V: string): void; }

export class AppMarkRead {
    public static readonly Key: string = "wac.markRead";
    public static readonly Attr: string = "data-wac-markread";
    public static readonly Words: string = "Mark chats read";
    public static readonly Tip: string = "Your contacts see blue ticks when you open their chat here. WhatsApp's own read-receipt setting still applies.";
    /** At most this many of their newest messages are asked about when a chat opens. */
    public static readonly MaxIds: number = 20;

    /** Is the switch on? Default ON; storage that cannot be read = ON (the default), never an error. */
    public static On = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Store: IMrStore | undefined = (globalThis as unknown as { localStorage?: IMrStore }).localStorage;
            if (Store === undefined) { return true; }
            return Store.getItem(AppMarkRead.Key) !== "0";
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: blocked storage (private mode, policy) throws on access; the default is the answer.
            return true;
        }
    };

    /** Store the choice. ERROR-SUPPRESSED-JUSTIFIED: blocked storage = the choice lasts this visit only (the item flips). */
    public static Set = (On: boolean): void => {
        try {
            const Store: IMrStore | undefined = (globalThis as unknown as { localStorage?: IMrStore }).localStorage;
            let Value: string = "0";
            if (On === true) { Value = "1"; }
            if (Store !== undefined) { Store.setItem(AppMarkRead.Key, Value); }
        } catch (e) {
            return;
        }
    };

    /** The menu item (a `menuitemcheckbox`, state in `aria-checked`). DOCUMENTED EXCEPTION: pure markup. */
    public static Item = (): string => {
        // ERROR-SUPPRESSED-JUSTIFIED: pure markup (`AppMenuCheck.Html`).
        return AppMenuCheck.Html(AppMarkRead.Attr, "1", AppMarkRead.On(), AppMarkRead.Tip, AppMarkRead.Words);
    };

    /** One delegated click listener on the document: the item toggles and says its new state. */
    public static Install = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: `AppMenuCheck.Listen` carries the guard and the handler.
        AppMenuCheck.Listen(AppMarkRead.OnClick, "AppMarkRead.Install");
    };

    public static OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: IMrNode | null = Ev.target as unknown as IMrNode | null;
            if (Target === null || typeof Target.closest !== "function") { return; }
            const Item: IMrNode | null = (Target.closest as (S: string) => IMrNode | null)("[" + AppMarkRead.Attr + "]");
            if (Item === null) { return; }
            const Next: boolean = AppMarkRead.On() === false;
            AppMarkRead.Set(Next);
            Item.setAttribute("aria-checked", String(Next));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMarkRead.OnClick");
        }
    };

    /**
     * A chat ask's body with `read` added: THEIR newest bubbles in the frame not asked about yet - only while the switch
     * is on (the feed asks only from a visible tab). The ids are remembered in `Asked` so each goes once per page.
     */
    public static WithRead = (Body: string, Root: { querySelectorAll(S: string): ArrayLike<{ getAttribute(N: string): string | null }> } | null,
                              Asked: Record<string, boolean>): string => {
        if (getCoreUtils().KErrorMode === true || Body.length === 0 || Root === null) { return Body; }
        try {
            if (AppMarkRead.On() === false) { return Body; }
            const Theirs: string[] = [];
            const All = Root.querySelectorAll(".b.them[data-mid]");
            for (let At: number = 0; At < All.length; At = At + 1) { Theirs.push(nz(All[At].getAttribute("data-mid"))); }
            const Ids: string[] = AppMarkRead.IdsFor(Theirs, Asked);
            if (Ids.length === 0) { return Body; }
            for (const Id of Ids) { Asked[Id] = true; }
            const Doc: Record<string, unknown> = JSON.parse(Body) as Record<string, unknown>;
            Doc.read = Ids;
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMarkRead.WithRead");
            return Body;
        }
    };

    /** The `read` ids for an ask: THEIR newest bubbles not asked about yet (`Asked` remembers them). Pure over the list. */
    public static IdsFor = (TheirIds: string[], Asked: Record<string, boolean>): string[] => {
        const Out: string[] = [];
        const From: number = Math.max(0, TheirIds.length - AppMarkRead.MaxIds);
        for (let At: number = From; At < TheirIds.length; At = At + 1) {
            const Id: string = nz(TheirIds[At]);
            if (/^[A-Za-z0-9]{8,64}$/.test(Id) === false || Asked[Id] === true) { continue; }
            Out.push(Id);
        }
        return Out;
    };
}
